import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import LinkButton from '../LinkButton';
import { api } from './api';
import ImportPanel from './ImportPanel';
import TaskCard from './TaskCard';
import TaskSettings from './TaskSettings';
import TurnsView from './TurnsView';
import TurnPanel from './TurnPanel';
import './Tasks.css';

// The Tasks screen of one quest: import box, lenses and state filters, the
// list (optionally grouped by point) and the open card — or, in the by-turns
// view, every voiced field by speaker turn with the cards on each turn and
// the re-voice passes — an overview whose turns open on the right
// (TurnPanel.js) to hear and judge their fragment. The URL carries the quest,
// the open card or turn and the view (`state` prop in, `onStateChange` out), so a link to a
// card lands on that card and a reload keeps the view.

export const LENSES = [['all', 'All'], ['audio', 'Audio'], ['text', 'Text'], ['media', 'Media'], ['other', 'Other']];
export const STATES = [['attention', 'Needs attention'], ['queued', 'Queued'], ['running', 'Running'], ['ready', 'Ready to review'], ['done', 'Done']];
const LIST_WIDTH_KEY = 'tasks.listWidth';
const LIST_MIN = 240;
const LIST_DEFAULT = 380;
const STATUS_LABEL = { open: 'Open', queued: 'Queued', running: 'Running', needs_attention: 'Needs attention', ready: 'Ready to review', done: 'Done', dismissed: 'Dismissed' };

const matchesState = (t, state) => {
  if (!state) return t.status !== 'dismissed';
  if (state === 'attention') return t.status === 'needs_attention' || t.status === 'open';
  if (state === 'queued') return t.status === 'queued';
  if (state === 'running') return t.status === 'running';
  if (state === 'ready') return t.status === 'ready';
  if (state === 'done') return t.status === 'done';
  return true;
};

export default function TasksScreen({ questId, questName, locations, currentUser, state, onStateChange, backHref, onBack, hrefs }) {
  const [tasks, setTasks] = useState([]);
  const [settings, setSettings] = useState(null);
  const [imports, setImports] = useState([]);
  const [usernames, setUsernames] = useState([]);
  const [quests, setQuests] = useState([]);
  const [error, setError] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [newDraft, setNewDraft] = useState({ title: '', summary: '', pointNo: '', field: '' });

  const selected = state.task || null;
  const lens = state.lens || 'all';
  const filter = state.state || '';
  const group = state.group === 'point';
  const point = state.point || null;
  const byTurns = state.view === 'turns';
  // The open turn of the by-turns view: `turn` = «<uuid>.<field>.<index>».
  const openTurn = useMemo(() => {
    const m = byTurns && state.turn ? String(state.turn).match(/^([0-9a-f-]{36})\.(description|after_message)\.(\d+)$/i) : null;
    return m ? { locationUuid: m[1], field: m[2], i: Number(m[3]) } : null;
  }, [byTurns, state.turn]);
  const [turnsFocus, setTurnsFocus] = useState(null);
  const set = useCallback((patch) => onStateChange({ ...state, ...patch }), [state, onStateChange]);
  // Width of the list column while a card or a turn is open on the right: a
  // drag on the divider sets it, the browser remembers it (per device).
  const [listWidth, setListWidth] = useState(() => { try { const v = Number(localStorage.getItem(LIST_WIDTH_KEY)); return v >= LIST_MIN ? v : LIST_DEFAULT; } catch (e) { return LIST_DEFAULT; } });
  const bodyRef = useRef(null);
  const startResize = (e) => {
    const body = bodyRef.current;
    if (!body) return;
    e.preventDefault();
    const left = body.getBoundingClientRect().left;
    const max = Math.max(LIST_MIN, body.clientWidth * 0.7);
    let width = listWidth;
    const move = (ev) => { width = Math.min(max, Math.max(LIST_MIN, Math.round(ev.clientX - left))); setListWidth(width); };
    const up = () => {
      window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); window.removeEventListener('pointercancel', up);
      document.body.style.cursor = ''; document.body.style.userSelect = '';
      try { localStorage.setItem(LIST_WIDTH_KEY, String(width)); } catch (err) { /* a private window: the width lives for the page */ }
    };
    document.body.style.cursor = 'col-resize'; document.body.style.userSelect = 'none';
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up); window.addEventListener('pointercancel', up);
  };

  const seqRef = useRef(0);
  const load = useCallback(async () => {
    const seq = ++seqRef.current;
    try {
      const r = await api.list(questId);
      if (seq !== seqRef.current) return;      // an older answer must not overwrite a newer one
      setTasks(r.tasks); setSettings(r.settings); setImports(r.imports); setError(null);
    } catch (e) { if (seq === seqRef.current) setError(e.message); }
  }, [questId]);
  useEffect(() => { load(); }, [load]);
  // The SSE stream was rebuilt (server restart, sleeping tab): events in
  // between are gone, so the whole list is read again.
  useEffect(() => {
    window.addEventListener('qv:resync', load);
    return () => window.removeEventListener('qv:resync', load);
  }, [load]);
  useEffect(() => {
    api.usernames().then(setUsernames).catch(() => {});
    api.quests().then((q) => setQuests(Array.isArray(q) ? q : (q.quests || []))).catch(() => {});
  }, []);
  useEffect(() => {
    const onEv = (e) => {
      const d = e.detail || {};
      if (d.type === 'task_updated' && d.task) setTasks((prev) => (prev.some((t) => t.id === d.task.id) ? prev.map((t) => (t.id === d.task.id ? { ...t, ...d.task } : t)) : [d.task, ...prev]));
      else if (d.type === 'task_deleted') setTasks((prev) => prev.filter((t) => t.id !== d.taskId));
      else if (d.type === 'task_settings' && d.settings) setSettings(d.settings);
      else if (d.type === 'task_import' && d.import) setImports((prev) => (prev.some((i) => i.id === d.import.id) ? prev.map((i) => (i.id === d.import.id ? d.import : i)) : [d.import, ...prev]));
      else if (d.type === 'task_run' && d.run) setTasks((prev) => prev.map((t) => (t.id === d.run.task_id ? { ...t, last_run: { run_id: d.run.id, run_status: d.run.status, run_stage: d.run.stage, recipe_id: d.run.recipe_id, attempt: d.run.attempt } } : t)));
      else if (d.type === 'task_message' && d.message) setTasks((prev) => prev.map((t) => (t.id === d.message.task_id ? { ...t, messages: (t.messages || 0) + 1, last_message_at: d.message.created_at } : t)));
    };
    window.addEventListener('qv:tasks', onEv);
    return () => window.removeEventListener('qv:tasks', onEv);
  }, []);

  const counts = useMemo(() => {
    const c = { attention: 0, queued: 0, running: 0, ready: 0, done: 0 };
    tasks.forEach((t) => { STATES.forEach(([k]) => { if (matchesState(t, k)) c[k] += 1; }); });
    return c;
  }, [tasks]);
  const visible = useMemo(() => tasks
    .filter((t) => (lens === 'all' ? true : t.categories.includes(lens)))
    .filter((t) => matchesState(t, filter))
    .filter((t) => (point ? (t.targets || []).some((g) => g.locationUuid === point) : true)), [tasks, lens, filter, point]);
  const byPoint = useMemo(() => {
    if (!group) return null;
    const m = new Map();
    const order = (locations || []).map((l) => l.uuid);
    visible.forEach((t) => {
      const keys = (t.targets || []).map((g) => g.locationUuid).filter(Boolean);
      (keys.length ? [...new Set(keys)] : ['__quest__']).forEach((k) => { if (!m.has(k)) m.set(k, []); m.get(k).push(t); });
    });
    return [...m.entries()].sort((a, b) => (a[0] === '__quest__' ? -1 : b[0] === '__quest__' ? 1 : order.indexOf(a[0]) - order.indexOf(b[0])));
  }, [visible, group, locations]);
  const pointLabel = (uuid) => { const i = (locations || []).findIndex((l) => l.uuid === uuid); return i >= 0 ? `${i + 1}. ${locations[i].name || ''}` : 'Unknown point'; };

  const openTurns = (uuid, field) => { setTurnsFocus(uuid ? { point: uuid, field: field || null } : null); set({ view: 'turns', point: null, field: null }); };
  const createTask = async () => {
    try {
      const body = { title: newDraft.title, summary: newDraft.summary, categories: ['other'], targets: newDraft.pointNo ? [{ pointNo: Number(newDraft.pointNo), field: newDraft.field || null, anchor: {} }] : [] };
      const r = await api.create(questId, body);
      setNewOpen(false); setNewDraft({ title: '', summary: '', pointNo: '', field: '' });
      await load();
      set({ task: r.task.id });
    } catch (e) { setError(e.message); }
  };

  const renderRow = (t) => (
    <button type="button" key={t.id} className={`tk-row-card ${selected === t.id ? 'selected' : ''} st-${t.status}`} onClick={() => set({ task: t.id })}>
      <div className="tk-row-top">
        <span className={`tk-chip tk-status st-${t.status}`}>{STATUS_LABEL[t.status] || t.status}</span>
        {t.categories.map((c) => <span key={c} className={`tk-chip cat-${c}`}>{c}</span>)}
        <span className={`tk-chip kind-${t.kind}`}>{t.kind}</span>
      </div>
      <div className="tk-row-title">{t.title}</div>
      <div className="tk-row-meta">
        {(t.targets || []).length ? <span>{[...new Set((t.targets || []).map((g) => g.pointNo).filter(Boolean))].slice(0, 6).map((n) => `p${n}`).join(' ')}{(t.targets || []).length > 6 ? ' …' : ''}</span> : <span>quest-wide</span>}
        {t.stage ? <span className="tk-muted"> · {t.stage}</span> : null}
        <span className="tk-grow" />
        <span className="tk-muted">{t.messages || 0} msg{t.assignee ? ` · @${t.assignee}` : ''}</span>
      </div>
    </button>
  );

  return (
    <div className="tk-page">
      <header className="tk-header">
        <LinkButton className="back-button" href={backHref} onClick={onBack}>Back to Editor</LinkButton>
        <div className="tk-header-title">
          <h2>Tasks <span className="tk-muted">· {questName || questId}</span></h2>
          {settings ? <span className="tk-muted">Autoexecutor: {settings.paused ? 'paused' : (settings.autorun ? 'starts new tasks on its own' : 'runs on request')}</span> : null}
        </div>
        <div className="tk-lenses tk-view-tabs" role="tablist" aria-label="View">
          <button type="button" role="tab" aria-selected={!byTurns} className={`tk-tab ${!byTurns ? 'on' : ''}`} onClick={() => set({ view: null, turn: null })}>Cards</button>
          <button type="button" role="tab" aria-selected={byTurns} className={`tk-tab ${byTurns ? 'on' : ''}`} onClick={() => set({ view: 'turns' })} title="Every voiced field by speaker turn: the remarks on each turn, the turns asked for, the fragments to judge; a turn opens with its chat">By turns</button>
        </div>
        <div className="tk-header-actions">
          <button type="button" className={`tk-btn ${showImport ? 'tk-btn-primary' : 'tk-btn-ghost'}`} onClick={() => setShowImport((v) => !v)}>Import remarks</button>
          <button type="button" className="tk-btn tk-btn-ghost" onClick={() => setNewOpen((v) => !v)}>New task</button>
          <button type="button" className={`tk-btn ${showSettings ? 'tk-btn-primary' : 'tk-btn-ghost'}`} onClick={() => setShowSettings((v) => !v)} aria-label="Autoexecutor settings">⚙ Settings</button>
        </div>
      </header>
      {error ? <div className="tk-error">{error}</div> : null}
      {showSettings && settings ? <TaskSettings settings={settings} onSave={async (p) => { const r = await api.saveSettings(questId, p); setSettings(r.settings); }} /> : null}
      {showImport ? <ImportPanel questId={questId} imports={imports} quests={quests} onImported={() => load()} onSelectTask={(id) => set({ task: id })} /> : null}
      {newOpen ? (
        <div className="tk-new">
          <input placeholder="Title" value={newDraft.title} onChange={(e) => setNewDraft((d) => ({ ...d, title: e.target.value }))} aria-label="Title" />
          <textarea placeholder="What is wrong / what to do (any language)" rows={2} value={newDraft.summary} onChange={(e) => setNewDraft((d) => ({ ...d, summary: e.target.value }))} aria-label="Description" />
          <div className="tk-row">
            <input placeholder="Point no." type="number" min={1} value={newDraft.pointNo} onChange={(e) => setNewDraft((d) => ({ ...d, pointNo: e.target.value }))} aria-label="Point number" style={{ width: 100 }} />
            <select value={newDraft.field} onChange={(e) => setNewDraft((d) => ({ ...d, field: e.target.value }))} aria-label="Field"><option value="">any field</option><option value="description">description</option><option value="after_message">after_message</option><option value="task">task</option><option value="hint">hint</option></select>
            <button type="button" className="tk-btn tk-btn-primary tk-btn-small" onClick={createTask} disabled={!newDraft.title.trim()}>Create</button>
            <button type="button" className="tk-btn tk-btn-ghost tk-btn-small" onClick={() => setNewOpen(false)}>Cancel</button>
          </div>
        </div>
      ) : null}

      {byTurns ? null : (
      <div className="tk-filters">
        <>
        <div className="tk-lenses" role="tablist" aria-label="Category">
          {LENSES.map(([k, label]) => <button type="button" key={k} role="tab" aria-selected={lens === k} className={`tk-tab ${lens === k ? 'on' : ''}`} onClick={() => set({ lens: k === 'all' ? null : k })}>{label}</button>)}
        </div>
        <div className="tk-states" role="tablist" aria-label="State">
          <button type="button" role="tab" aria-selected={!filter} className={`tk-tab ${!filter ? 'on' : ''}`} onClick={() => set({ state: null })}>Any state</button>
          {STATES.map(([k, label]) => <button type="button" key={k} role="tab" aria-selected={filter === k} className={`tk-tab ${filter === k ? 'on' : ''}`} onClick={() => set({ state: k })}>{label} <span className="tk-count">{counts[k]}</span></button>)}
        </div>
        <label className="tk-check"><input type="checkbox" checked={group} onChange={(e) => set({ group: e.target.checked ? 'point' : null })} /> Group by point</label>
        {point ? <span className="tk-chip">point: {pointLabel(point)} <button type="button" className="tk-linkbtn" onClick={() => set({ point: null })} aria-label="Clear the point filter">✕</button></span> : null}
        </>
      </div>
      )}

      <div ref={bodyRef} className={`tk-body ${selected || openTurn ? 'with-card' : ''}`} style={selected || openTurn ? { gridTemplateColumns: `${listWidth}px 8px minmax(0, 1fr)` } : undefined}>
        {byTurns ? (
          <div className="tk-list" aria-label="Turns">
            <TurnsView questId={questId} filter={state.turns || ''} onFilter={(v) => set({ turns: v || null })} focus={turnsFocus || (state.point ? { point: state.point, field: state.field || null } : null)} onFocusHandled={() => setTurnsFocus(null)} openTurn={openTurn} onOpenTurn={(t) => set({ task: null, turn: `${t.locationUuid}.${t.field}.${t.i}` })} onOpenCard={(id) => set({ task: id })} />
          </div>
        ) : (
        <div className="tk-list" aria-label="Tasks">
          {visible.length === 0 ? <div className="tk-empty">{tasks.length ? 'Nothing matches these filters.' : 'No tasks yet. Import remarks or create a task.'}</div> : null}
          {group && byPoint ? byPoint.map(([k, list]) => (
            <div key={k} className="tk-group">
              <div className="tk-group-head">{k === '__quest__' ? 'Whole quest' : pointLabel(k)} <span className="tk-muted">{list.length}</span></div>
              {list.map(renderRow)}
            </div>
          )) : visible.map(renderRow)}
        </div>
        )}
        {selected || openTurn ? <div className="resizer tk-resizer" onPointerDown={startResize} title="Drag to resize" /> : null}
        {openTurn ? (
          <div className="tk-detail" style={selected ? { display: 'none' } : undefined}>
            <TurnPanel key={`${openTurn.locationUuid}.${openTurn.field}.${openTurn.i}`} questId={questId} locationUuid={openTurn.locationUuid} field={openTurn.field} turnIndex={openTurn.i} usernames={usernames} currentUser={currentUser} onOpenCard={(id) => set({ task: id })} onClose={() => set({ turn: null })} />
          </div>
        ) : null}
        {selected ? (
          <div className="tk-detail">
            {openTurn ? <button type="button" className="tk-btn tk-btn-primary" onClick={() => set({ task: null })}>← Back to the turn</button> : null}
            <TaskCard taskId={selected} usernames={usernames} currentUser={currentUser} hrefs={hrefs} allTasks={tasks} onChanged={load} onOpenTurns={openTurns} onOpenTurn={(t) => set({ view: 'turns', task: null, turn: `${t.locationUuid}.${t.field}.${t.i}` })} onClose={(next) => set({ task: typeof next === 'string' ? next : null })} />
          </div>
        ) : null}
      </div>
    </div>
  );
}
